/**
 * DataHealthRowDrawer — ficha de detalle de un ítem oculto (Data Health / Ocultos).
 *
 * Usa el modo 2 (ItemDetailSplitPane, overlay 50% dentro del contenedor) + el cuerpo de
 * ficha COMPARTIDO (DataHealthFichaBody). Tabs: Resumen / Enriquecimiento / Historial.
 * Header: título = SOLO id_catalogo (spec Gabriel 2026-08-07); el badge de "oculto por"
 * queda como acción del header.
 */
import React, { useEffect, useMemo, useState } from 'react';
import type { DataHealthItem, HistoryEvent } from '../../../types/dataHealth';
import { getHistory, isBackendUnavailable, errorMessage } from '../../../services/dataHealth';
import { ItemDetailSplitPane } from '../../common/ItemDetail';
import {
  DataHealthFichaBody,
  DATA_HEALTH_FICHA_TABS,
  HistorialView,
  type FichaTab,
  type FichaHistoryEvent,
  type FichaEventKind,
} from './DataHealthFicha';
import { StatusBadge } from '../shared/inspectorTableHelpers';

interface Props {
  clientId: string;
  item: DataHealthItem;
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  hasPrev?: boolean;
  hasNext?: boolean;
}

const DataHealthRowDrawer: React.FC<Props> = ({
  clientId,
  item,
  onClose,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
}) => {
  const [tab, setTab] = useState<FichaTab>('resumen');

  const hiddenBadge =
    item.visibility === 0 && item.hidden_reason ? (
      <StatusBadge
        kind={
          item.hidden_reason === 'hidden_out_of_scope' || item.hidden_reason === 'out_of_scope'
            ? 'hidden_out_of_scope'
            : item.hidden_reason === 'hidden_low_confidence' || item.hidden_reason === 'low_confidence'
              ? 'hidden_low_confidence'
              : item.hidden_reason === 'hidden_duplicate' || item.hidden_reason === 'duplicate'
                ? 'hidden_duplicate'
                : 'hidden_manual'
        }
      />
    ) : undefined;

  return (
    <ItemDetailSplitPane
      title={`Catalog ID: ${item.id_catalogo}`}
      headerActions={hiddenBadge}
      width="w-[65%]"
      tabs={DATA_HEALTH_FICHA_TABS}
      activeTab={tab}
      onTabChange={(id) => setTab(id as FichaTab)}
      onClose={onClose}
      onPrev={onPrev}
      onNext={onNext}
      hasPrev={hasPrev}
      hasNext={hasNext}
    >
      <DataHealthFichaBody
        clientId={clientId}
        item={item}
        tab={tab}
        historial={<OcultosHistorial clientId={clientId} idCatalogo={item.id_catalogo} />}
      />
    </ItemDetailSplitPane>
  );
};

// ----------------------------------------------------------------------------
// Historial (Ocultos): carga vía getHistory y adapta a FichaHistoryEvent.
// ----------------------------------------------------------------------------

const OcultosHistorial: React.FC<{ clientId: string; idCatalogo: string }> = ({ clientId, idCatalogo }) => {
  const [events, setEvents] = useState<HistoryEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [backendDown, setBackendDown] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    setBackendDown(false);
    getHistory(clientId, idCatalogo)
      .then((r) => {
        if (!cancelled) setEvents(r.events);
      })
      .catch((e) => {
        if (cancelled) return;
        if (isBackendUnavailable(e)) setBackendDown(true);
        else setErr(errorMessage(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, idCatalogo]);

  const adapted: FichaHistoryEvent[] = useMemo(
    () =>
      events.map((ev) => ({
        id: ev.id,
        label: labelForEventType(ev.eventType),
        kind: kindForEventType(ev.eventType, ev.payload),
        actor: ev.actor || ev.actorKind,
        at: ev.createdAt,
        detail: <EventPayload payload={ev.payload} />,
      })),
    [events]
  );

  return <HistorialView events={adapted} loading={loading} error={err} backendDown={backendDown} />;
};

function labelForEventType(t: HistoryEvent['eventType']): string {
  switch (t) {
    case 'catalog_created':
      return 'Catalogado (creación)';
    case 'catalog_recataloged':
      return 'Re-catalogado';
    case 'manual_edit':
      return 'Edición manual';
    case 'visibility_changed':
      return 'Visibilidad cambió';
    case 'auto_hide_rule_applied':
      return 'Regla auto-hide';
    case 'toma_first_seen':
      return 'Primera vez en toma';
    case 'outlier_flag':
      return 'Outlier flag';
    case 'url_corrected':
      return 'URL corregida';
    default:
      return String(t);
  }
}

// Mapea el eventType del backend al `kind` semántico del timeline (ícono).
function kindForEventType(
  t: HistoryEvent['eventType'],
  payload?: Record<string, unknown>
): FichaEventKind {
  switch (t) {
    case 'catalog_created':
      return 'created';
    case 'catalog_recataloged':
      return 'recataloged';
    case 'manual_edit':
      return 'manual_edit';
    case 'visibility_changed': {
      // Si viró a visible → ojo abierto; si viró a oculto → ojo tachado.
      const to = (payload as { to?: { visibility?: number } })?.to?.visibility;
      return to === 1 ? 'visibility_shown' : 'visibility_hidden';
    }
    case 'auto_hide_rule_applied':
      return 'auto_hide';
    case 'toma_first_seen':
      return 'first_seen';
    case 'outlier_flag':
      return 'outlier';
    case 'url_corrected':
      return 'url';
    default:
      return 'generic';
  }
}

const EventPayload: React.FC<{ payload: Record<string, unknown> }> = ({ payload }) => {
  const changes = payload?.changes as Record<string, { before?: unknown; after?: unknown }> | undefined;
  if (changes && typeof changes === 'object') {
    return (
      <div className="space-y-1">
        {Object.entries(changes).map(([field, diff]) => (
          <DiffRow key={field} field={field} before={diff.before} after={diff.after} />
        ))}
      </div>
    );
  }
  const reason = (payload?.reason ?? (payload as { to?: { reason?: string } })?.to?.reason) as string | undefined;
  if (reason) {
    return (
      <div className="text-[10px] text-gray-600">
        <span className="text-gray-400">motivo:</span> {reason}
      </div>
    );
  }
  return null;
};

// Fila de diff before→after legible: campo + valor viejo (tachado, gris) → valor nuevo.
export const DiffRow: React.FC<{ field: string; before?: unknown; after?: unknown }> = ({
  field,
  before,
  after,
}) => (
  <div className="flex items-center gap-1.5 flex-wrap text-[10px]">
    <span className="text-gray-500 font-medium">{field}</span>
    <span className="inline-flex items-center rounded bg-gray-100 text-gray-400 line-through px-1 py-0.5">
      {formatVal(before)}
    </span>
    <span className="text-gray-400">→</span>
    <span className="inline-flex items-center rounded bg-emerald-50 text-emerald-700 px-1 py-0.5 font-medium">
      {formatVal(after)}
    </span>
  </div>
);

function formatVal(v: unknown): string {
  if (v == null || v === '') return 'null';
  if (typeof v === 'string') return v.length > 40 ? v.slice(0, 40) + '…' : v;
  return String(v);
}

export default DataHealthRowDrawer;
